/**
 * Minimal relative-time formatter for snapshot timestamps.
 *
 * We don't pull in `date-fns` / `dayjs` / `luxon` for three buckets of output
 * ("just now", "N minutes/hours/days ago", "absolute date") — the dependency
 * weight isn't worth it. If the design later asks for full i18n or
 * business-day math, swap the implementation behind this signature without
 * touching callers.
 *
 * Contract:
 * - Accepts an ISO-8601 string (that's what the backend emits for `capturedAt`).
 * - Returns a human-readable string in English.
 * - Future dates (clock skew) are treated as "just now" rather than producing
 *   "in 3 minutes" — a snapshot can't be captured in the future from the user's
 *   point of view, so we avoid a weird UI.
 * - Anything older than ~6 days falls back to an absolute date, because after
 *   that point "relative" becomes less informative than the actual date.
 */

const MS = {
  minute: 60_000,
  hour: 60 * 60_000,
  day: 24 * 60 * 60_000,
  week: 7 * 24 * 60 * 60_000,
} as const;

/**
 * Format a relative duration between `isoTimestamp` and `now`.
 *
 * @param isoTimestamp ISO-8601 datetime string (e.g. `2026-04-21T14:33:00.000Z`).
 * @param now Reference date — injected so tests are deterministic.
 */
export function formatRelativeTime(isoTimestamp: string, now: Date = new Date()): string {
  const captured = new Date(isoTimestamp).getTime();
  const diffMs = now.getTime() - captured;

  // Future timestamps (clock skew, mock data): snap to "just now" so we
  // never emit "in 3 minutes" — the snapshot history panel reads strictly
  // as a past log.
  if (diffMs < MS.minute) return 'just now';
  if (diffMs < MS.hour) {
    const minutes = Math.floor(diffMs / MS.minute);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diffMs < MS.day) {
    const hours = Math.floor(diffMs / MS.hour);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffMs < MS.week) {
    const days = Math.floor(diffMs / MS.day);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  // Past 6 days "N days ago" loses precision — fall back to the real date.
  return new Date(captured).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Absolute timestamp suitable for a tooltip next to the relative label.
 * Exposed separately so callers can render both ("2 hours ago" + title
 * "Tuesday, April 21, 2026, 14:33").
 */
export function formatAbsoluteTime(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Compact, line-fittable date+time (e.g. `Apr 21, 14:33`) for rendering
 * alongside a relative label. The absolute form belongs visibly in the
 * UI — a relative label alone is ambiguous once users accumulate many
 * snapshots, and burying the date in a tooltip is not discoverable.
 *
 * Omitting the year keeps the line short and matches typical usage where
 * users are scanning their own recent history. If snapshots older than
 * the current year become common, the year could be appended
 * conditionally — out of scope for the moment.
 */
export function formatCompactDateTime(isoTimestamp: string): string {
  return new Date(isoTimestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
